import React, { useRef, useState, useEffect } from 'react';
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { BsArrowRightShort } from 'react-icons/bs';
import { Button } from '../../../core/Button/Button';
import './CardElement.scss';
import master from '../../../assets/master.png';
import { TransactionDetails } from '../../../types/api.types';
import { sendCardDetailsAPI } from '../../../services/cardpayment.service';
import { logEvent } from '../../../analytics';
import { toast } from 'react-toastify';
import { encryptMessage } from '../../../utils/cardEncryption.util';
import { oneticketValidateAPI } from '../../../services/oneticket.service';
import { insertTransactionData } from '../../../configs/firebaseConfig';

export const CARD = [
  'MASTERCARD',
  'MASTERCARD-USD',
  'NTB-AMEX-LKR',
  'NTB-AMEX-USD',
  'MASTERCARD-FUNDING',
];

function getFormattedDateTime() {
  const date = new Date();

  // Get components
  const year = date.getFullYear();
  // Months are zero-indexed, so add 1
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  // Construct the date-time string
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}
const getCardType = (num: any): string => {
  if (typeof num !== 'string') {
    return 'Invalid';
  }

  let number = num.replace(/\s/g, '');
  const cardPatterns = {
    visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
    mastercard: /^(5[1-5][0-9]{14}|222[0-9]{13}|555[0-9]{13})$/,
    amex: /^3[47][0-9]{13}$/,
  };

  if (cardPatterns.visa.test(number)) {
    return 'Visa';
  } else if (cardPatterns.mastercard.test(number)) {
    return 'MasterCard';
  } else if (cardPatterns.amex.test(number)) {
    return 'Amex';
  } else {
    return 'Invalid';
  }
};

const getMaxLength = (type: string) => {
  switch (type) {
    case 'Visa':
    case 'MasterCard':
      return 16;
    case 'Amex':
      return 15;
    default:
      return 16;
  }
};

const formatCardNumber = (number: string, type: string) => {
  if (type === 'Visa' || type === 'MasterCard') {
    return number.replace(/(\d{4})/g, '$1 ').trim();
  } else if (type === 'Amex') {
    return number.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3').trim();
  } else {
    return number;
  }
};

const validateBIN = (cardNumber: string, allowedBINs: string[]): boolean => {
  if (!allowedBINs || allowedBINs.length === 0) return true;

  const cleanCardNumber = cardNumber.replace(/\s/g, '');
  return allowedBINs.some((bin) => cleanCardNumber.startsWith(bin));
};

const getValidationSchema = (
  paymentDetails: TransactionDetails,
  installmentData: any,
  discountApplied: any
) =>
  Yup.object().shape({
    nameOnCard: Yup.string()
      .required('Name on card is required')
      .max(32, 'Name on card must be at most 32 characters'),
    number: Yup.string()
      .required('Card number is required')
      .test(
        'card-type',
        'Invalid card number',
        (value) => getCardType(value || '') !== 'Invalid'
      )
      .test(
        'installment-bin-validation',
        'Card not eligible for installment with this bank',
        function (value) {
          // Only validate BIN if installment is selected and has specific bank BINs
          if (
            installmentData?.isInstallmentSelected &&
            installmentData?.bankBins &&
            installmentData?.bankBins?.length > 0
          ) {
            return validateBIN(value || '', installmentData.bankBins);
          }
          // For one-time payments or when no installment selected, skip BIN validation
          return true;
        }
      )
      .test(
        'discount-bin-validation',
        'Card not eligible for discount with this bank',
        function (value) {
          // Check if discount is applied and has specific bank BINs
          if (
            discountApplied?.bank_bins &&
            Array.isArray(discountApplied.bank_bins) &&
            discountApplied.bank_bins.length > 0
          ) {
            return validateBIN(value || '', discountApplied.bank_bins);
          }
          // If no BIN restrictions for discount, validation passes
          return true;
        }
      ),
    year: Yup.string().required('Expiry year is required'),
    securityCode: Yup.string()
      .required('Security code is required')
      .test('len', 'Invalid security code length', function (value: any) {
        const { number } = this.parent;
        const cardType = getCardType(number);
        if (cardType === 'Amex') {
          return value && value.length === 4;
        } else {
          return value && value.length === 3;
        }
      }),
  });

interface FormValues {
  nameOnCard: string;
  number: string;
  month: string;
  year: string;
  securityCode: string;
}

const ExpiryDateInput: React.FC<{
  field: any;
  form: any;
  inputRef: React.Ref<HTMLInputElement>;
  onExpiryComplete: () => void;
}> = ({ field, form, inputRef, onExpiryComplete }) => {
  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');

    if (value.length >= 4) {
      value = `${value.substring(0, 2)}/${value.substring(2, 4)}`;
      onExpiryComplete();
    }

    form.setFieldValue(field.name, value);
  };

  return (
    <input
      {...field}
      type="text"
      onChange={handleExpiryDateChange}
      maxLength={5}
      placeholder="MM/YY"
      className="w-100 txt-field"
      ref={inputRef}
      inputMode="numeric" // This will trigger the numeric keypad
    />
  );
};

interface elementResponse {
  response: any;
}

const handleNameInputChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => {
  const value = e.target.value.replace(/[0-9]/g, '');
  setFieldValue('nameOnCard', value);
};

const handleCVVInputChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => {
  const value = e.target.value.replace(/\D/g, '');
  setFieldValue('securityCode', value);
};

export const CardElement = ({ response }: elementResponse) => {
  const [cardType, setCardType] = useState<string>('');
  const expiryDateRef = useRef<HTMLInputElement>(null);
  const cvvRef = useRef<HTMLInputElement>(null);
  const paymentDetails: TransactionDetails = useSelector(
    (state: any) => state.payment
  );

  const installmentData = useSelector((state: any) => state.installment);
  console.log(installmentData);

  // Get discount applied data from Redux store
  const discountApplied = paymentDetails?.data?.discount_applied;

  const initialValues: FormValues = {
    nameOnCard: '',
    number: '',
    month: '',
    year: '',
    securityCode: '',
  };

  const getDeviceDetails = () => {
    const deviceDetails = {
      browser: navigator.userAgent,
      browserDetails: {
        javaEnabled: navigator.javaEnabled().toString(),
        javaScriptEnabled: 'true', // Since React relies on JavaScript
        language: navigator.language,
        screenHeight: window.screen.height.toString(),
        screenWidth: window.screen.width.toString(),
        timeZone: (new Date().getTimezoneOffset() * -1).toString(),
        colorDepth: window.screen.colorDepth.toString(),
        // Assuming full screen for the 3DSecureChallengeWindowSize
        '3DSecureChallengeWindowSize': 'FULL_SCREEN',
      },
    };

    return deviceDetails;
  };

  const onSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    // Handle form submission
    logEvent('User', 'Clicked Button', 'Payment Button');

    //  if (paymentDetails.data.request_type == "TICKETING") {
    let oneticketData = {
      onepay_transaction_id: paymentDetails.data.onepay_transaction_id,
    };

    const validate_response: any = await oneticketValidateAPI(oneticketData);
    if (validate_response.status !== 200) {
      toast.error('Sorry you took too long to submit payment.', {
        autoClose: 3000,
      });

      const transactionDetails = {
        is_pay: false, // Set the value accordingly
        merchant_transaction_id: paymentDetails.data.request_transaction_id,
        tran_date: getFormattedDateTime(),
      };
      insertTransactionData(
        paymentDetails.data.onepay_transaction_id,
        transactionDetails
      );
      // Add a small delay before redirecting
      setTimeout(() => {
        window.location.href = paymentDetails.data.transaction_redirect_url;
      }, 3000); // Adjust the delay as needed for your toast duration

      return;
    }

    // }

    let expiredate = values.year.split('/');
    const gatewayType =
      getCardType(values.number) == 'Amex' ? 'NTB-AMEX' : 'MASTERCARD';

    // Find the provider_id based on the gateway type
    const getProviderId = (cardType: string) => {
      if (installmentData.providerID !== 0) {
        return installmentData.providerID;
      }
      if (
        paymentDetails.data.provider_keys &&
        Array.isArray(paymentDetails.data.provider_keys)
      ) {
        // Handle new format (array of objects)
        if (
          paymentDetails.data.provider_keys.length > 0 &&
          typeof paymentDetails.data.provider_keys[0] === 'object'
        ) {
          const providerKeys = paymentDetails.data.provider_keys as Array<{
            id: number;
            key: string;
          }>;

          // Determine if it's a USD payment
          const isUSD = paymentDetails.data.currency === 'USD';

          // Construct the provider key based on card type and currency
          let providerKey: string;

          if (cardType === 'Visa' || cardType === 'MasterCard') {
            providerKey = isUSD ? 'MASTERCARD-USD' : 'MASTERCARD';
          } else if (cardType === 'Amex') {
            providerKey = isUSD ? 'NTB-AMEX-USD' : 'NTB-AMEX-LKR';
          } else {
            return null; // Invalid card type
          }

          console.log('Provider key to search for:', providerKey);
          const provider = providerKeys.find(
            (provider) => provider.key === providerKey
          );
          return provider ? provider.id : null;
        }
      }
      return null;
    };

    let requestData = {
      onepay_transaction_id: paymentDetails.data.onepay_transaction_id,
      app_id: paymentDetails.data.app.app_id,
      reference_id: paymentDetails.data.request_transaction_id,
      gateway_type: gatewayType,
      card_type: getCardType(values.number) == 'Amex' ? 'NTB' : 'MASTERCARD',
      card_brand: getCardType(values.number),
      provider_id: getProviderId(getCardType(values.number)),
      card: encryptMessage({
        month: expiredate[0],
        year: expiredate[1],
        nameOnCard: values.nameOnCard,
        number: values.number.replace(/\s/g, ''),
        securityCode: values.securityCode,
      }),
      device: {
        browserDetails: {},
      },
      ...(installmentData?.isInstallmentSelected &&
        installmentData?.selectedPlanId && {
          installment_plan_id: installmentData.selectedPlanId,
        }),
    };
    const card_response = await sendCardDetailsAPI(requestData);

    if (card_response.status === 1000) {
      await response(card_response);
    } else {
      toast.error('Error Occured:' + card_response.message);
      logEvent('Error', '3DS error', card_response);
    }

    setSubmitting(false);
  };

  const handleCardNumberChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => void,
    setFieldTouched: (
      field: string,
      isTouched?: boolean,
      shouldValidate?: boolean
    ) => void
  ) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const cardType = getCardType(rawValue);
    setCardType(cardType);
    const formattedValue = formatCardNumber(rawValue, cardType);
    await setFieldValue('number', formattedValue, true);
    setFieldTouched('number', true, true);

    if (rawValue.length === getMaxLength(cardType)) {
      expiryDateRef.current?.focus();
    }
  };

  const handleExpiryComplete = () => {
    cvvRef.current?.focus();
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(
          paymentDetails,
          installmentData,
          discountApplied
        )}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form>
            <div className="row ms-3 me-3">
              <label className="card-lbl ps-0">Name on Card</label>
              <Field
                type="text"
                name="nameOnCard"
                className="txt-field"
                placeholder="Enter card name"
                maxLength={32}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleNameInputChange(e, formik.setFieldValue)
                }
              />
              <ErrorMessage
                className="error"
                name="nameOnCard"
                component="div"
              />
            </div>

            <div className="row ms-3 me-3">
              <label className="card-lbl ps-0">Card Number</label>
              <div className="d-flex justify-content-between txt-field">
                <Field
                  type="text"
                  name="number"
                  className="number-field w-75"
                  placeholder="**** **** **** ****"
                  inputMode="numeric" // This will trigger the numeric keypad
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleCardNumberChange(
                      e,
                      formik.setFieldValue,
                      formik.setFieldTouched
                    )
                  }
                />
                <div
                  id="input--cc"
                  className={`creditcard-icon--${cardType.toLowerCase()}`}
                ></div>
              </div>
              <ErrorMessage className="error" name="number" component="div" />
            </div>

            <div className="row ms-3 me-3">
              <div className="col-6 ps-0">
                <div>
                  <label className="card-lbl ps-0">Expire Date</label>
                  <Field name="year">
                    {({ field, form }: { field: any; form: any }) => (
                      <ExpiryDateInput
                        field={field}
                        form={form}
                        inputRef={expiryDateRef}
                        onExpiryComplete={handleExpiryComplete}
                      />
                    )}
                  </Field>
                  <ErrorMessage className="error" name="year" component="div" />
                </div>
              </div>

              <div className="col-6 pe-0">
                <div className="w-100">
                  <label className="card-lbl ps-0">CVV</label>
                  <Field
                    type="password"
                    name="securityCode"
                    className="w-100 txt-field"
                    placeholder="Enter CVV"
                    inputMode="numeric"
                    innerRef={cvvRef}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleCVVInputChange(e, formik.setFieldValue)
                    }
                  />
                  <ErrorMessage
                    className="error"
                    name="securityCode"
                    component="div"
                  />
                </div>
              </div>

              <div className="ps-0 pe-0">
                <Button
                  buttonText={'Pay'}
                  type="submit"
                  icon={<BsArrowRightShort />}
                  className="pay-btn"
                  style={{
                    backgroundColor:
                      paymentDetails.data.app.action_button_bt_color,
                  }}
                  isLoading={formik.isSubmitting}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
